import React, { useState, useEffect, useRef } from "react";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import logoLapyno from "@/assets/logo-lapyno.png";
import { supabase } from "@/integrations/supabase/client";
import { sendVerificationCode, verifyEmailCode } from "@/modules/core/data/emailVerificationRepository";
import { toast } from "sonner";
import { AuthLayout, AuthButton } from "@/components/auth";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

interface EmailVerificationProps {
  email: string;
  onVerified: () => void;
  onLogout: () => void;
  onBack?: () => void;
}

export function EmailVerification({ email, onVerified, onLogout, onBack }: EmailVerificationProps) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [waitingForSession, setWaitingForSession] = useState(true);
  
  // Ref to prevent double-send (React StrictMode / re-renders)
  const sendInitiatedRef = useRef(false);
  
  // Check if too many attempts error - block input
  const isTooManyAttempts = error?.includes("Trop de tentatives");

  // Wait for session and send code on mount (runs once)
  useEffect(() => {
    // Prevent double execution
    if (sendInitiatedRef.current) return;
    sendInitiatedRef.current = true;
    
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 10;
    
    const waitForSessionAndSendCode = async () => {
      const checkSession = async (): Promise<boolean> => {
        const { data: { session } } = await supabase.auth.getSession();
        return !!session?.access_token;
      };
      
      while (isMounted && retryCount < maxRetries) {
        const hasSession = await checkSession();
        if (hasSession) {
          if (isMounted) {
            setWaitingForSession(false);
            sendCode();
          }
          return;
        }
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      if (isMounted && retryCount >= maxRetries) {
        setWaitingForSession(false);
        setError("Session non disponible. Veuillez vous reconnecter.");
      }
    };
    
    waitForSessionAndSendCode();
    
    return () => {
      isMounted = false;
    };
  }, []); // No dependencies - runs once on mount

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendCode = async (isResend = false) => {
    if (countdown > 0 && isResend) return;
    
    setIsSending(true);
    setError(null);

     try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setError("Session expirée. Veuillez vous reconnecter.");
          setIsSending(false);
        return;
      }
        
        console.log("[EmailVerification] Sending code for email:", email);

      const result = await sendVerificationCode(email, isResend);
        
        console.log("[EmailVerification] Response:", result);

      if (result.error) {
        setError(result.error);
        return;
      }

      setCodeSent(true);
      setCountdown(30);
      
      if (isResend) {
        toast.success("Nouveau code envoyé !");
      }
    } catch (err: any) {
      console.error("Error sending code:", err);
      setError("Impossible d'envoyer le code. Veuillez réessayer.");
    } finally {
      setIsSending(false);
    }
  };

   const verifyCodeHandler = async () => {
    if (code.length !== 4) return;

    setIsVerifying(true);
    setError(null);

    try {
      const result = await verifyEmailCode(code);

      if (result.error) {
        setError(result.error);
        setCode("");
        return;
      }

      toast.success("E-mail vérifié avec succès !");
      onVerified();
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError("Une erreur est survenue. Veuillez réessayer.");
      setCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  // Auto-verify when code is complete
  useEffect(() => {
    if (code.length === 4 && !isVerifying) {
      verifyCodeHandler();
    }
  }, [code]);

  return (
    <AuthLayout>
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      )}

      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center">
          <a href="https://lapyno.com" target="_blank" rel="noopener noreferrer">
            <img 
              src={logoLapyno} 
              alt="Lapyno" 
              className="h-16 w-auto object-contain mb-6 hover:opacity-80 transition-opacity cursor-pointer" 
            />
          </a>
          
          <h1 className="text-2xl font-bold text-foreground text-center">
            Inscrivez-vous en moins d'une minute
          </h1>
          <p className="text-sm text-muted-foreground text-center mt-2">
            <Mail className="inline-block h-4 w-4 mr-1 -mt-0.5" />
            Si vous ne recevez pas l'e-mail, pensez à vérifier vos <strong>courriers indésirables (spams)</strong>.
          </p>
        </div>

        {/* Loading state while waiting for session */}
        {waitingForSession ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
            <p className="text-muted-foreground text-center">
              Préparation de l'envoi du code...
            </p>
          </div>
        ) : (
          <>
        {/* OTP Input */}
        <div className="flex justify-center">
          <InputOTP
            maxLength={4}
            value={code}
            onChange={(value) => setCode(value)}
            disabled={isVerifying || isTooManyAttempts}
          >
            <InputOTPGroup className="gap-3">
              <InputOTPSlot index={0} className="w-14 h-14 text-2xl border-2 border-violet-300 rounded-lg bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200" />
              <InputOTPSlot index={1} className="w-14 h-14 text-2xl border-2 border-violet-300 rounded-lg bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200" />
              <InputOTPSlot index={2} className="w-14 h-14 text-2xl border-2 border-violet-300 rounded-lg bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200" />
              <InputOTPSlot index={3} className="w-14 h-14 text-2xl border-2 border-violet-300 rounded-lg bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-200" />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {/* Description */}
        <div className="text-center space-y-1">
          <p className="text-muted-foreground">
            Nous venons de vous envoyer un code par e-mail. Consultez votre boîte de messagerie.
          </p>
          <p className="font-medium text-foreground">{email}</p>
        </div>

        {/* Error message */}
        {error && (
          <p className="text-center text-sm text-red-600">{error}</p>
        )}

        {/* Confirm button */}
        <AuthButton 
          onClick={verifyCodeHandler} 
          loading={isVerifying} 
          disabled={code.length !== 4 || isVerifying}
        >
          Confirmer
        </AuthButton>

        {/* Resend / Logout links */}
        <div className="text-center text-sm text-muted-foreground">
          Vous ne voyez pas de code ?{" "}
          <button
            onClick={() => sendCode(true)}
            disabled={countdown > 0 || isSending}
            className={`font-medium transition-colors ${
              countdown > 0 || isSending
                ? "text-gray-400 cursor-not-allowed"
                : "text-violet-600 hover:text-violet-700 hover:underline"
            }`}
          >
            {isSending ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Envoi...
              </span>
            ) : countdown > 0 ? (
              `Renvoyer le code (${countdown}s)`
            ) : (
              "Renvoyer le code"
            )}
          </button>
          {" "}ou{" "}
          <button
            onClick={onLogout}
            className="text-violet-600 hover:text-violet-700 font-medium hover:underline"
          >
            Se déconnecter
          </button>
        </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
